import { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Book, TrendingUp, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import JournalChat from '../components/JournalChat';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Journal = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('calendar'); // calendar, write, read
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailySummary, setDailySummary] = useState(null);
  const [monthlyInsights, setMonthlyInsights] = useState(null);

  useEffect(() => {
    fetchEntries();
  }, [currentDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchDailySummary(selectedDate);
    }
  }, [selectedDate]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch entries for current month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await fetch(
        `http://localhost:5000/api/journal/entries?startDate=${startOfMonth.toISOString()}&endDate=${endOfMonth.toISOString()}&status=completed`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (data.entries) {
        // Map the conversation field to messages for frontend compatibility
        const mappedEntries = data.entries.map(entry => ({
          ...entry,
          messages: entry.conversation?.map(conv => ({
            role: conv.role,
            content: conv.message
          })) || []
        }));
        setEntries(mappedEntries);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySummary = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const dateStr = date.toISOString().split('T')[0];
      
      const response = await fetch(
        `http://localhost:5000/api/journal/daily-summary?date=${dateStr}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setDailySummary(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch daily summary:', error);
    }
  };

  const fetchMonthlyInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const response = await fetch(
        `http://localhost:5000/api/journal/monthly-insights?month=${month}&year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setMonthlyInsights(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch monthly insights:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const hasEntryOnDate = (date) => {
    if (!date) return false;
    return entries.some(entry => {
      const entryDate = new Date(entry.entryDate || entry.createdAt);
      return entryDate.toDateString() === date.toDateString();
    });
  };

  const getEntriesForDate = (date) => {
    return entries.filter(entry => {
      const entryDate = new Date(entry.entryDate || entry.createdAt);
      return entryDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    
    // If clicking today, allow writing option
    // If clicking past/future, only show view
    const today = new Date();
    if (date.toDateString() === today.toDateString() && !hasEntryOnDate(date)) {
      // Ask user if they want to write or view
      setView('calendar'); // Stay on calendar to see the day details
    } else {
      setView('calendar'); // Just view the selected day
    }
  };

  const handleReadEntry = (date) => {
    setSelectedDate(date);
    setView('read');
  };

  const handleNewEntry = () => {
    // Only allow writing entries for today
    const today = new Date();
    setSelectedDate(today);
    setView('write');
  };

  const canWriteForDate = (date) => {
    // Only allow writing for today
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);

  // Read view - full screen entry display
  if (view === 'read') {
    const dayEntries = getEntriesForDate(selectedDate);
    
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-4">
          <button
            onClick={() => setView('calendar')}
            className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Calendar
          </button>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                📖 {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h2>
              <p className="text-sm text-gray-500">Reading your journal entry</p>
            </div>

            {dayEntries.length > 0 ? (
              <div className="space-y-6">
                {dayEntries.map((entry, index) => (
                  <div key={entry._id} className="space-y-4">
                    {/* Header with mood and time */}
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      {entry.mood && (
                        <span className="text-4xl">
                          {entry.mood === 'very_happy' && '😄'}
                          {entry.mood === 'happy' && '😊'}
                          {entry.mood === 'neutral' && '😐'}
                          {entry.mood === 'sad' && '😢'}
                          {entry.mood === 'anxious' && '😰'}
                        </span>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Entry #{index + 1}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.entryDate || entry.createdAt).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Full conversation */}
                    {entry.messages && entry.messages.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          💬 Conversation
                        </h3>
                        {entry.messages.map((msg, msgIdx) => (
                          <div 
                            key={msgIdx} 
                            className={`p-4 rounded-lg ${
                              msg.role === 'user' 
                                ? 'bg-blue-50 border border-blue-100' 
                                : 'bg-gray-50 border border-gray-100'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">
                                {msg.role === 'user' ? '👤' : '🤖'}
                              </span>
                              <span className="font-semibold text-sm">
                                {msg.role === 'user' ? 'You' : 'AI Assistant'}
                              </span>
                            </div>
                            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                              {msg.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Summary */}
                    {entry.summary && (
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-purple-900 uppercase tracking-wide mb-2">
                          📝 Summary
                        </h3>
                        <p className="text-purple-800 leading-relaxed">{entry.summary}</p>
                      </div>
                    )}

                    {/* Activities */}
                    {entry.activities && entry.activities.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                          🏃 Activities
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {entry.activities.map((activity, i) => (
                            <span key={i} className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {entry.tags && entry.tags.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                          🏷️ Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {entry.tags.map((tag, i) => (
                            <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* AI Daily Summary */}
                {dailySummary && (
                  <div className="mt-8 bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🤖</span>
                      AI Daily Summary
                    </h3>
                    <p className="text-purple-800 leading-relaxed mb-4">{dailySummary.summary}</p>
                    {dailySummary.insights && dailySummary.insights.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-2">💡 Insights</h4>
                        <ul className="space-y-2">
                          {dailySummary.insights.map((insight, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-purple-600 mt-1">•</span>
                              <span className="text-purple-800">{insight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No entries for this day</p>
                {canWriteForDate(selectedDate) && (
                  <button
                    onClick={() => setView('write')}
                    className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Write Today's Entry
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'write') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-4">
          <button
            onClick={() => {
              setView('calendar');
              fetchEntries();
            }}
            className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Calendar
          </button>
          <JournalChat onComplete={() => {
            setView('calendar');
            fetchEntries();
          }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Book className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Digital Diary 📔</h1>
                <p className="text-sm text-gray-600">Track your daily life with AI insights</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasEntryOnDate(selectedDate) && (
                <button
                  onClick={() => handleReadEntry(selectedDate)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <BookOpen className="w-5 h-5" />
                  Read Entry
                </button>
              )}
              <button
                onClick={handleNewEntry}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Write Today's Entry
              </button>
            </div>
          </div>

          {/* Monthly Insights Button */}
          <button
            onClick={fetchMonthlyInsights}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <TrendingUp className="w-5 h-5" />
            Get Monthly Insights
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handlePreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day names */}
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {days.map((date, index) => {
                const hasEntry = hasEntryOnDate(date);
                const isTodayDate = isToday(date);
                const isSelectedDate = isSelected(date);
                
                return (
                  <div
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={`
                      aspect-square flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer
                      transition-all relative
                      ${!date ? 'cursor-default' : ''}
                      ${hasEntry ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}
                      ${isTodayDate ? 'ring-2 ring-blue-600 font-bold' : ''}
                      ${isSelectedDate ? 'bg-blue-600 text-white' : 'text-gray-700'}
                    `}
                  >
                    {date && (
                      <>
                        <span className={`text-sm ${isSelectedDate ? 'text-white' : ''}`}>
                          {date.getDate()}
                        </span>
                        {hasEntry && (
                          <div className={`w-1.5 h-1.5 rounded-full mt-1 ${
                            isSelectedDate ? 'bg-white' : 'bg-blue-600'
                          }`} />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 ring-2 ring-blue-600 rounded" />
                <span>Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                </div>
                <span>Has entry</span>
              </div>
            </div>
          </div>

          {/* Day View / Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              {getEntriesForDate(selectedDate).length > 0 && (
                <button
                  onClick={() => handleReadEntry(selectedDate)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Read Full
                </button>
              )}
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-4">
                {getEntriesForDate(selectedDate).length > 0 ? (
                  <>
                    <div className="text-xs text-gray-500 mb-2">
                      Found {getEntriesForDate(selectedDate).length} entry(ies)
                    </div>
                    {getEntriesForDate(selectedDate).map((entry, index) => (
                      <div key={entry._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          {entry.mood && (
                            <span className="text-2xl">
                              {entry.mood === 'very_happy' && '😄'}
                              {entry.mood === 'happy' && '😊'}
                              {entry.mood === 'neutral' && '😐'}
                              {entry.mood === 'sad' && '😢'}
                              {entry.mood === 'anxious' && '😰'}
                            </span>
                          )}
                          <span className="text-sm text-gray-500">
                            {new Date(entry.entryDate || entry.createdAt).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        {/* Show full conversation messages */}
                        {entry.messages && entry.messages.length > 0 ? (
                          <div className="space-y-2 mb-3">
                            <h4 className="text-xs font-semibold text-gray-700 uppercase">Conversation</h4>
                            {entry.messages.map((msg, msgIdx) => (
                              <div 
                                key={msgIdx} 
                                className={`p-2 rounded-lg text-sm ${
                                  msg.role === 'user' 
                                    ? 'bg-blue-50 text-blue-900' 
                                    : 'bg-gray-50 text-gray-800'
                                }`}
                              >
                                <div className="font-medium text-xs mb-1">
                                  {msg.role === 'user' ? '👤 You' : '🤖 AI'}
                                </div>
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 mb-3 italic">
                            No conversation messages available
                          </div>
                        )}

                        {/* Summary */}
                        {entry.summary && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-1">Summary</h4>
                            <p className="text-sm text-gray-700">{entry.summary}</p>
                          </div>
                        )}

                        {/* Activities */}
                        {entry.activities && entry.activities.length > 0 && (
                          <div className="mb-2">
                            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-1">Activities</h4>
                            <div className="flex flex-wrap gap-1">
                              {entry.activities.map((activity, i) => (
                                <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                  {activity}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tags */}
                        {entry.tags && entry.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {entry.tags.map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Debug info - show raw entry structure */}
                        <details className="mt-3 text-xs">
                          <summary className="cursor-pointer text-gray-400 hover:text-gray-600">
                            Debug: View entry data
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                            {JSON.stringify(entry, null, 2)}
                          </pre>
                        </details>
                      </div>
                    ))}
                    
                    {dailySummary && (
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-semibold text-purple-900 mb-2">🤖 AI Daily Summary</h4>
                        <p className="text-sm text-purple-800">{dailySummary.summary}</p>
                        {dailySummary.insights && (
                          <div className="mt-2 space-y-1">
                            {dailySummary.insights.map((insight, i) => (
                              <p key={i} className="text-xs text-purple-700">• {insight}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No entries for this day</p>
                    {canWriteForDate(selectedDate) ? (
                      <button
                        onClick={handleNewEntry}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Write Today's Entry
                      </button>
                    ) : (
                      <p className="mt-4 text-sm text-gray-400">
                        {selectedDate > new Date() 
                          ? "You can only write entries for today" 
                          : "No entry was written on this day"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Monthly Insights Modal */}
        {monthlyInsights && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  📊 Monthly Insights - {monthNames[currentDate.getMonth()]}
                </h2>
                <button
                  onClick={() => setMonthlyInsights(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">Summary</h3>
                  <p className="text-blue-800">{monthlyInsights.summary}</p>
                </div>

                {monthlyInsights.moodTrends && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">😊 Mood Trends</h3>
                    <p className="text-green-800">{monthlyInsights.moodTrends}</p>
                  </div>
                )}

                {monthlyInsights.achievements && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h3 className="font-semibold text-yellow-900 mb-2">🏆 Achievements</h3>
                    <ul className="space-y-1">
                      {monthlyInsights.achievements.map((achievement, i) => (
                        <li key={i} className="text-yellow-800">• {achievement}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {monthlyInsights.recommendations && (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-2">💡 Recommendations</h3>
                    <ul className="space-y-1">
                      {monthlyInsights.recommendations.map((rec, i) => (
                        <li key={i} className="text-purple-800">• {rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Journal;
